import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client/react";
import { useFormikContext } from "formik";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import Input from "../../form/Input";
import { MOVEWARE_STORAGE_ACCOUNT_QUERY } from "../../../constants/graphql/queries";

const formatStatus = status =>
  status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : "Not provided";

const formatDate = date => {
  const match = date?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : date || "Not provided";
};

const SectionHeader = ({ children }) => (
  <Typography sx={{ fontSize: 17, fontWeight: 500 }}>{children}</Typography>
);

const DetailText = ({ children, sx }) => (
  <Typography sx={{ fontSize: 16, ...sx }}>{children}</Typography>
);

const codesFor = branchCode =>
  String(branchCode || "")
    .toUpperCase()
    .match(/[A-Z0-9]+/g) || [];

const matchingClientId = (clients, branchCode) => {
  if (!branchCode) return "";

  const normalizedBranch = String(branchCode).trim().toUpperCase();
  return clients.find(client => codesFor(client.branchCode).includes(normalizedBranch))?.id || "";
};

export default function MovewareStorageAccountField({
  clients = [],
  populateClientName = false,
  onConfirm,
  confirmLabel = "Confirm Account",
  confirming = false,
  onPreviewChange,
}) {
  const { setFieldValue, values } = useFormikContext();
  const [storageAccount, setStorageAccount] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const [confirmedStorageAccountId, setConfirmedStorageAccountId] = useState(null);
  const [fetchStorageAccount, { loading }] = useLazyQuery(MOVEWARE_STORAGE_ACCOUNT_QUERY, {
    fetchPolicy: "network-only",
  });
  const previewVisible =
    Boolean(storageAccount) &&
    storageAccount.storageAccountId === values.movewareStorageAccountId;

  useEffect(() => {
    onPreviewChange?.(previewVisible);
    return () => onPreviewChange?.(false);
  }, [onPreviewChange, previewVisible]);

  useEffect(() => {
    if (
      confirmedStorageAccountId &&
      confirmedStorageAccountId !== values.movewareStorageAccountId.trim()
    ) {
      setConfirmedStorageAccountId(null);
      setFieldValue("externalRecordId", "");
    }
  }, [confirmedStorageAccountId, setFieldValue, values.movewareStorageAccountId]);

  const handleLookup = async () => {
    const storageAccountId = values.movewareStorageAccountId.trim();
    setFieldValue("movewareStorageAccountId", storageAccountId);
    setFieldValue("externalRecordId", "");
    if (clients.length) setFieldValue("clientId", "");
    setConfirmedStorageAccountId(null);
    setStorageAccount(null);
    setLookupError(null);

    try {
      const response = await fetchStorageAccount({ variables: { id: storageAccountId } });
      if (response.error) {
        setLookupError(response.error.message);
        return;
      }

      setStorageAccount(response.data.movewareStorageAccount);
    } catch (error) {
      setLookupError(error.message);
    }
  };

  const handleConfirm = async () => {
    try {
      await onConfirm?.(storageAccount);
    } catch {
      return;
    }

    setConfirmedStorageAccountId(storageAccount.storageAccountId);
    setFieldValue("externalRecordId", storageAccount.externalRecordId);
    const clientId =
      storageAccount.matchedClient?.id || matchingClientId(clients, storageAccount.branch);
    if (clientId) setFieldValue("clientId", clientId);
    if (!values.projectName && storageAccount.projectName) {
      setFieldValue("projectName", storageAccount.projectName);
    }
    if (populateClientName && !values.clientName && storageAccount.customerName) {
      setFieldValue("clientName", storageAccount.customerName);
    }
  };

  return (
    <>
      <Grid size={9}>
        <Input
          name="movewareStorageAccountId"
          label="Moveware Storage Account ID"
          autoComplete="off"
          showNAButton={false}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          onKeyDown={event => {
            if (event.key === "Enter") {
              event.preventDefault();
              if (values.movewareStorageAccountId) handleLookup();
            }
          }}
        />
      </Grid>
      <Grid size={3}>
        <Button
          fullWidth
          sx={{ height: 40 }}
          type="button"
          variant="outlined"
          disabled={!values.movewareStorageAccountId || loading}
          aria-label={loading ? "Looking up Moveware storage account" : "Look up"}
          onClick={handleLookup}
        >
          {loading ? <CircularProgress size={20} /> : "Look up"}
        </Button>
      </Grid>
      {lookupError && (
        <Grid size={12}>
          <Alert severity="error">{lookupError}</Alert>
        </Grid>
      )}
      {storageAccount &&
        storageAccount.storageAccountId === values.movewareStorageAccountId && (
          <Grid size={12}>
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
              <Box>
                <SectionHeader>Branch</SectionHeader>
                <DetailText>{storageAccount.branch || "Not provided"}</DetailText>
              </Box>

              <Box sx={{ mt: 2.5 }}>
                <SectionHeader>Customer</SectionHeader>
                <DetailText>
                  Name: {storageAccount.customerName || "Not provided"}
                </DetailText>
                <DetailText>
                  ID: {storageAccount.customerId || "Not provided"} (
                  {formatStatus(storageAccount.customerStatus)})
                </DetailText>
              </Box>

              <Box sx={{ mt: 2.5 }}>
                <SectionHeader>Storage Account</SectionHeader>
                <DetailText>
                  Name: {storageAccount.storageAccountName || "Not provided"}
                </DetailText>
                <DetailText>
                  ID: {storageAccount.storageAccountId} ({formatStatus(storageAccount.status)})
                </DetailText>
                <DetailText>Date In: {formatDate(storageAccount.dateIn)}</DetailText>
                <DetailText>
                  Order Number: {storageAccount.orderNumber || "Not provided"}
                </DetailText>
                <DetailText>
                  Reference In: {storageAccount.moveId || "Not provided"}
                </DetailText>
                <DetailText sx={{ whiteSpace: "pre-wrap" }}>
                  Notes: {storageAccount.notes || "Not provided"}
                </DetailText>
              </Box>

              {storageAccount.alreadyLinked ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  This storage account is already linked to{" "}
                  {storageAccount.linkedProject?.name || "another project"}.
                </Alert>
              ) : !storageAccount.eligible ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Both the Moveware customer and storage account must be active.
                </Alert>
              ) : (
                <Button
                  sx={{ mt: 2 }}
                  type="button"
                  variant="contained"
                  disabled={
                    confirming || confirmedStorageAccountId === storageAccount.storageAccountId
                  }
                  onClick={handleConfirm}
                >
                  {confirmedStorageAccountId === storageAccount.storageAccountId
                    ? "Account confirmed"
                    : confirmLabel}
                </Button>
              )}
            </Box>
          </Grid>
        )}
    </>
  );
}
